import { useTranslation } from 'react-i18next'
import { ACTION_TYPE_BY_ITEM_STATUS, ChangeHistoryItem } from '../../../../domain/items'
import styled from 'styled-components'
import { Card, Col, Row } from 'antd'
import React from 'react'
import Icons from '../../../../../../../Main/Components/Icons'
import moment from 'moment'
import { InfoCircleOutlined } from '@ant-design/icons'

const getIconCardBackgroundColorByActionType = (actionType: string): string => {
  switch (actionType) {
    case ACTION_TYPE_BY_ITEM_STATUS.REPLACED_BY_PICKER:
      return '#B2DFDB'
    default:
      return '#B4E6FD'
  }
}

const getImageOpacityByHistoryItem = (historyItem: ChangeHistoryItem, replacementItemCard: boolean): string => {
  let imageOpacity = '1'

  if (!replacementItemCard && historyItem.replacementItem.length === 0) {
    imageOpacity = '1'
  }

  if (!replacementItemCard && historyItem.replacementItem.length > 0) {
    imageOpacity = '0.6'
  }

  return imageOpacity
}

const getTextColorByHistoryItem = (historyItem: ChangeHistoryItem, replacementItemCard: boolean): string => {
  let textColor = '#000000D9'

  if (!replacementItemCard && historyItem.replacementItem.length === 0) {
    textColor = '#000000D9'
  }

  if (!replacementItemCard && historyItem.replacementItem.length > 0) {
    textColor = '#00000073'
  }

  return textColor
}
const getBannerStylesByHistoryItem = (actionType: string): BannerStyle => {
  switch (actionType) {
    case ACTION_TYPE_BY_ITEM_STATUS.REPLACED_BY_PICKER:
      return {
        borderColor: '#87E8DE',
        backgroundColor: '#E6FFFB',
        iconColor: '#1677FF'
      }
    default:
      return {
        borderColor: '#91D5FF',
        backgroundColor: '#E6F7FF',
        iconColor: '#1677FF'
      }
  }
}

const HistoryItemCardSection = styled.div`
  background: #ffffff;
  margin-bottom: 1rem;
  border-radius: 1rem;

  & > div {
    border-radius: 1rem;
    & > div {
      padding: 0;
    }
  }
`

const HeaderInformationSection = styled.div<{ actionType: string }>`
  & > div {
    & > div:nth-child(1) {
      padding: 0.8rem 1.6rem;
      & > label {
        color: #00000073;
        font-size: 1.2rem;
        font-style: normal;
        font-weight: 600;
      }
    }
    & > div:nth-child(2) {
      & > div {
        width: 100%;
        text-align: center;
        & > div {
          background: ${({ actionType }) => getIconCardBackgroundColorByActionType(actionType)};
          height: 3.2rem;
          width: 3.2rem;
          border-radius: 0 0.8rem 0 40%;
          float: right;
          & > svg {
            vertical-align: middle;
          }
        }
      }
    }
  }
`

const ReasonTitleSection = styled.div`
  padding: 0.8rem 1.6rem;
  & > div {
    & > div {
      & > label {
        font-size: 1.4rem;
        font-style: normal;
        font-weight: 500;
        color: #000000d9;
      }
    }
  }
`

const ItemSection = styled.div<{ historyItem: ChangeHistoryItem; replacementItemCard?: boolean }>`
  padding: 0.8rem 1.6rem;
  & > div:nth-child(1) {
    margin-bottom: 1.8rem;
    & > div:nth-child(1) {
      & > div:nth-child(1) {
        & > img {
          opacity: ${({ historyItem, replacementItemCard }) =>
            getImageOpacityByHistoryItem(historyItem, replacementItemCard)};
        }
      }
    }
    & > div:nth-child(2) {
      margin-left: 1.6rem;
      & > label:nth-child(1) {
        color: ${({ historyItem, replacementItemCard }) => getTextColorByHistoryItem(historyItem, replacementItemCard)};
        font-size: 1.4rem;
        font-style: normal;
        font-weight: 600;
        display: block;
      }
      & > label:nth-child(2) {
        color: ${({ historyItem, replacementItemCard }) => getTextColorByHistoryItem(historyItem, replacementItemCard)};
        font-size: 1.4rem;
        font-style: normal;
        font-weight: 400;
        display: block;
      }
    }
  }
  & > label:nth-child(2) {
    padding: 1.4rem 2.6rem;
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 500;
    color: #000000d9;
  }
  & > div:nth-child(3) {
  }
`

const BannerInformationSection = styled.div<{ actionType: string }>`
  padding: 0 1.6rem 0.8rem;
  & > div {
    & > div {
      & > div {
        border: ${({ actionType }) => `solid 0.1rem ${getBannerStylesByHistoryItem(actionType).borderColor}`};
        border-left: ${({ actionType }) => `solid 0.4rem ${getBannerStylesByHistoryItem(actionType).borderColor}`};
        background: ${({ actionType }) => getBannerStylesByHistoryItem(actionType).backgroundColor};
        padding: 1rem 0.8rem;
        border-radius: 0.8rem;
        & > div {
          & > div:nth-child(1) {
            text-align: center;
            & > span {
              color: ${({ actionType }) => getBannerStylesByHistoryItem(actionType).iconColor};
            }
          }
          & > div:nth-child(2) {
            & > label {
              font-size: 1.4rem;
              font-style: normal;
              font-weight: 500;
              color: #000000d9;
            }
          }
        }
      }
    }
  }
`

type BannerStyle = {
  borderColor: string
  backgroundColor: string
  iconColor: string
}

type HistoryItemCardProps = {
  historyItem: ChangeHistoryItem
}

const HistoryItemCard = (props: HistoryItemCardProps) => {
  const { historyItem } = props
  const { t } = useTranslation()

  const getHistoryItemCardIconByActionType = (actionType: string): React.ReactNode => {
    switch (actionType) {
      case ACTION_TYPE_BY_ITEM_STATUS.REPLACED_BY_PICKER:
        return <Icons iconName='ResourceChooseReplacementGreen' />
      default:
        return <Icons iconName='ReplacementByClientBlue' />
    }
  }

  return (
    <HistoryItemCardSection>
      <Card>
        <HeaderInformationSection actionType={historyItem.actionType}>
          <Row>
            <Col span={21}>
              <label>{moment(historyItem.date).format('HH:mm | MMM D, YYYY')}</label>
            </Col>
            <Col span={3}>
              <div>
                <div>{getHistoryItemCardIconByActionType(historyItem.actionType)}</div>
              </div>
            </Col>
          </Row>
        </HeaderInformationSection>

        <ReasonTitleSection>
          <Row>
            <Col span={24}>
              <label>{t<string>(historyItem.changeBy)}</label>
            </Col>
          </Row>
        </ReasonTitleSection>

        {historyItem.item.map((item) => (
          <ItemSection historyItem={historyItem}>
            <Row>
              <Col span={4}>
                <div>
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{ height: '6.4rem', width: '6.4rem', borderRadius: '0.8rem' }}
                  />
                </div>
              </Col>
              <Col span={18}>
                <label>{item.name}</label>
                <label>{item.description}</label>
              </Col>
            </Row>

            {historyItem.replacementItem.map((replacementItem) => (
              <>
                <label>
                  <Icons iconName='ArrowReplaceItem' /> {t<string>('jobs.timeline.items.replacedBy.replacedBy')}:
                </label>

                <ItemSection historyItem={historyItem} replacementItemCard={true}>
                  <Row>
                    <Col span={4}>
                      <div>
                        <img
                          src={replacementItem.image}
                          alt={replacementItem.name}
                          style={{ height: '6.4rem', width: '6.4rem', borderRadius: '0.8rem' }}
                        />
                      </div>
                    </Col>
                    <Col span={18}>
                      <label>{replacementItem.name}</label>
                      <label>{replacementItem.description}</label>
                    </Col>
                  </Row>
                </ItemSection>
              </>
            ))}
          </ItemSection>
        ))}

        <BannerInformationSection actionType={historyItem.actionType}>
          <Row>
            <Col span={24}>
              <div>
                <Row>
                  <Col span={2}>
                    <InfoCircleOutlined />
                  </Col>
                  <Col span={22}>
                    <label>{t<string>(historyItem.changeBy)}</label>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </BannerInformationSection>
      </Card>
    </HistoryItemCardSection>
  )
}

export { HistoryItemCard }
